import { useState, useEffect } from 'react';
import { useToasts } from '@blaze-react/toaster';
import { FaTimes } from 'react-icons/fa';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { PRODUCT_BOAT_PRO } from './constants';
import { pushToDataLayer, submitSalesforceLead, checkValidEmail } from '../../utils';

const BoatProForm = ({ description }) => {
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [submitState, setSubmitState] = useState(null);

  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [website, setWebsite] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(null);

  const [companyError, setCompanyError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [firstNameError, setFirstNameError] = useState(null);
  const [lastNameError, setLastNameError] = useState(null);
  const [websiteError, setWebsiteError] = useState(null);

  const inputClass = 'boat-pro-form__input';
  const inputWrapperClass = 'boat-pro-form__input-wrapper';
  const doubleInputWrapperClass = 'boat-pro-form__double-input-wrapper';
  const errorClass = 'boat-pro-form__error';

  const companyInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!companyError,
    [inputClass]: !companyError
  });

  const emailInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!emailError,
    [inputClass]: !emailError
  });

  const firstNameInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!firstNameError,
    [inputClass]: !firstNameError
  });

  const lastNameInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!lastNameError,
    [inputClass]: !lastNameError
  });

  const websiteInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!websiteError,
    [inputClass]: !websiteError
  });

  const handleKeyDown = event => {
    if (event.keyCode !== 13) return;

    setSubmitState({ target: event.target });
  };

  const submit = async (passedEmail, passedFirstName, passedLastName, passedCompany, passedWebsite, passedPhoneNumber) => {
    try {
      await submitSalesforceLead(
        passedEmail,
        PRODUCT_BOAT_PRO,
        passedFirstName,
        passedLastName,
        passedCompany,
        passedWebsite,
        passedPhoneNumber,
        null,
        null,
        null
      );

      setSuccessMessageVisible(true);

      pushToDataLayer({ event: 'Lead Generated', leadSource: PRODUCT_BOAT_PRO });

      return true;
    } catch {
      addToast('Error while submitting the form, please try again', {
        appearance: 'error',
        autoDismiss: true
      });

      return false;
    }
  };

  useEffect(
    () => {
      if (submitState === null) return;

      let valid = true;
      if (!company) {
        setCompanyError('Required field');
        valid = false;
      }
      if (!email) {
        setEmailError('Required field');
        valid = false;
      } else if (!checkValidEmail(email)) {
        setEmailError('Invalid email format');
        valid = false;
      }
      if (!firstName) {
        setFirstNameError('Required field');
        valid = false;
      }
      if (!lastName) {
        setLastNameError('Required field');
        valid = false;
      }
      if (!website) {
        setWebsiteError('Required field');
        valid = false;
      }
      if (!valid) {
        setSubmitState(null);
        return;
      }

      if (submitState.target) {
        submitState.target.blur();
      }

      (async () => {
        const successful = await submit(email, firstName, lastName, company, website, phoneNumber);

        setSubmitState(null);

        if (successful) {
          setCompany('');
          setEmail('');
          setFirstName('');
          setLastName('');
          setWebsite('');
          setPhoneNumber(null);
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [submitState, email, firstName, lastName, company, website, phoneNumber]
  );

  return (
    <>
      {successMessageVisible && <div className="boat-pro-form__success-message">
        <p className="boat-pro-form__success-message__paragraph">
          Thank you for completing our contact form.
        </p>

        <p className="boat-pro-form__success-message__paragraph">
          We will be in contact with you shortly.
        </p>
      </div>}

      {!successMessageVisible && (
        <>
          {submitState && <div className="boat-pro-form__overlay" />}

          <div className="boat-pro-form__fields-wrapper">
            <div className={doubleInputWrapperClass}>
              <div className={inputWrapperClass}>
                <input
                  className={firstNameInputClass}
                  type="text"
                  value={firstName}
                  onChange={event => setFirstName(event.target.value)}
                  onKeyDown={event => {
                    setFirstNameError(null);
                    handleKeyDown(event);
                  }}
                  placeholder="* First Name"
                />

                {firstNameError && <p className={errorClass}>{firstNameError}</p>}
              </div>

              <div className={inputWrapperClass}>
                <input
                  className={lastNameInputClass}
                  type="text"
                  value={lastName}
                  onChange={event => setLastName(event.target.value)}
                  onKeyDown={event => {
                    setLastNameError(null);
                    handleKeyDown(event);
                  }}
                  placeholder="* Last Name"
                />

                {lastNameError && <p className={errorClass}>{lastNameError}</p>}
              </div>
            </div>

            <div className={inputWrapperClass}>
              <input
                className={inputClass}
                type="text"
                value={phoneNumber || ''}
                onChange={event => setPhoneNumber(event.target.value)}
                onKeyDown={event => handleKeyDown(event)}
                placeholder="Phone Number"
              />
            </div>

            <div className={inputWrapperClass}>
              <input
                className={emailInputClass}
                type="text"
                value={email}
                onChange={event => setEmail(event.target.value)}
                onKeyDown={event => {
                  setEmailError(null);
                  handleKeyDown(event);
                }}
                placeholder="* Email"
              />

              {emailError && <p className={errorClass}>{emailError}</p>}
            </div>

            <div className={inputWrapperClass}>
              <input
                className={companyInputClass}
                type="text"
                value={company}
                onChange={event => setCompany(event.target.value)}
                onKeyDown={event => {
                  setCompanyError(null);
                  handleKeyDown(event);
                }}
                placeholder="* Company"
              />

              {companyError && <p className={errorClass}>{companyError}</p>}
            </div>

            <div className={inputWrapperClass}>
              <input
                className={websiteInputClass}
                type="text"
                value={website}
                onChange={event => setWebsite(event.target.value)}
                onKeyDown={event => {
                  setWebsiteError(null);
                  handleKeyDown(event);
                }}
                placeholder="* Website"
              />

              {websiteError && <p className={errorClass}>{websiteError}</p>}
            </div>

            <a className="boat-pro-form__submit" onClick={() => setSubmitState({})}>
              Submit
            </a>
          </div>
        </>
      )}
    </>
  );
};

BoatProForm.propTypes = {
  description: PropTypes.string.isRequired
};

export default BoatProForm;
